import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, User, Eye, EyeOff, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useAdmin } from '../../context/AdminContext';

const AdminLoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAdmin();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(username, password);
    
    if (result.success) {
      navigate('/admin/dashboard');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-stone-900 flex items-center justify-center p-4" data-testid="admin-login-page">
      {/* Background Pattern */}
      <div className="fixed inset-0 opacity-5"
        style={{
          backgroundImage: `
            repeating-linear-gradient(45deg, transparent, transparent 40px, #fbbf24 40px, #fbbf24 42px),
            repeating-linear-gradient(-45deg, transparent, transparent 40px, #dc2626 40px, #dc2626 42px)
          `
        }}
      ></div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-black text-white tracking-tight">AMLARIA</h1>
          <p className="text-amber-400 text-sm tracking-[0.3em] mt-1">ADMIN PANEL</p>
        </div>

        {/* Login Card */}
        <div className="bg-stone-800 border border-stone-700 shadow-2xl">
          <div className="h-1 bg-gradient-to-r from-amber-600 via-red-600 to-amber-600"></div>
          
          <div className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-amber-600/20 flex items-center justify-center">
                <Lock className="h-5 w-5 text-amber-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Welcome Back</h2>
                <p className="text-stone-400 text-sm">Sign in to access the control center</p>
              </div>
            </div>

            {error && (
              <div className="mb-6 p-3 bg-red-900/30 border border-red-700 text-red-300 text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="username" className="text-stone-300">Username</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-500" />
                  <Input
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter username"
                    required
                    className="pl-10 bg-stone-900 border-stone-600 text-white placeholder:text-stone-500 rounded-none focus:border-amber-500"
                    data-testid="admin-username"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-stone-300">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-500" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    required
                    className="pl-10 pr-10 bg-stone-900 border-stone-600 text-white placeholder:text-stone-500 rounded-none focus:border-amber-500"
                    data-testid="admin-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-300"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-5 rounded-none"
                data-testid="admin-login-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>
          </div>
        </div>

        <p className="text-center text-stone-500 text-xs mt-6">
          Amlaria E-Commerce Control Center © 2024
        </p>
      </div>
    </div>
  );
};

export default AdminLoginPage;
